import { cloneDeep, isEqual } from 'lodash';
import Vue from 'vue';
import { buildCriteria, prepareNodesAndLinking, prepareScene } from '../../helpers/callFlow';
import { validateNode, getParameterValidationFields, validateLinkCriterias } from '../../helpers/validation';
import { sanitizeNodeSettings } from '../../helpers/save';

/**
 * 
 * @param {Object} node 
 * @returns import('../../ivr').NodeLinkDetails
 */
function getNodeLinkDetail(node) {
  return {
    x: node.x,
    y: node.y,
    outputs: node.outputs,
    nodeOptions: node.nodeOptions,
    goToNodeId: node.settings?.goToNodeId,
    id: node.id,
    nodeTypeId: node.nodeTypeId
  };
}

/**
 * 
 * @param {Array<Object>} nodes 
 * @returns Object.<string, import('../../ivr').NodeLinkDetails>
 */
function convertNodesToNodeLinkDetails(nodes) {
  return nodes.reduce((nodeLinksMap, node) => {
    const nodeLinkDetails = getNodeLinkDetail(node);

    nodeLinksMap[node.id] = nodeLinkDetails;

    return nodeLinksMap;
  }, {});
}

/**
 * 
 * @param {Object} oldNode 
 * @param {Object} newNode 
 * @returns boolean
 */
function areNodeSettingsEqual(oldNode = {}, newNode = {}) {
  // If it's a new node
  if (!oldNode?.settings) {
    return true;
  }

  return isEqual(sanitizeNodeSettings(oldNode.settings), sanitizeNodeSettings(newNode.settings)) && oldNode?.stepName === newNode.stepName;
}

const state = {
  /**
   * @type {Array<Object>}
   */
  originalNodes: [],

  /**
   * @type {string}
   */
  name: '',

  nodes: [],

  /**
   * @type {Array<import('../../ivr').NodeLink>}
   */
  links: [],

  /**
   * @type {import('../../ivr').SceneContainer}
   */
  container: {
    centerX: 0,
    centerY: 0,
    scale: 1
  },

  selectedNode: null,

  /**
   * @type {Object.<string, import('../../ivr').NodeLinkDetails>}
   */
  nodeLinkDetails: {},

  /**
   * @type {Object.<string, boolean>}
   */
  nodesSettingsChangedMapping: {},

  /**
   * @type {boolean}
   */
  hasLinksChanged: false,

  /**
   * @type {boolean}
   */
  hasNodeAddedOrRemoved: false,

  /**
   * @type {boolean}
   */
  hasNodePositionChanged: false,

  /**
   * @type {object}
   */
  nodeErrors: {},

  /**
   * @type {object}
   */
  nodesValidationMapping: {},
};

/** 
 * @type {import('vuex').GetterTree<typeof state>} 
 */
const getters = {
  /**
   * Validates nodes
   * @param {typeof state} state 
   * @param {typeof getters} getters 
   * @returns Object.<string, import('../../ivr').NodeError>
   */
  nodeErrors(state) {
    return state.nodeErrors;
  },

  scene(state) {
    return {
      name: state.name,
      nodes: state.nodes,
      links: state.links,
      container: state.container,
      nodeLinkDetails: state.nodeLinkDetails
    };
  },

  nodesValidationMapping(state) {
    return state.nodesValidationMapping;
  },

  /**
   * 
   * @param {typeof state} state 
   * @param {typeof getters} getters 
   * @param {Object} rootState 
   * @returns Array.<{ errorMessage: String }>
   */
  callFlowErrors(state, getters, rootState) {
    if (Object.values(state.nodeErrors).some(validation => validation.isSuccessful === false)) {
      return [{
        errorMessage: 'One or more Nodes failed validation'
      }];
    }

    return rootState['callFlows/sceneErrors'];
  },

  hasNodeSettingsChanged(state) {
    return Object.keys(state.nodesSettingsChangedMapping).some(nodeId => {
      return state.nodesSettingsChangedMapping[nodeId];
    });
  },

  isDirty(state, getters) {
    return state.hasNodeAddedOrRemoved || state.hasNodePositionChanged || getters.hasNodeSettingsChanged || state.hasLinksChanged;
  }
};

/**
 * @type {import('vuex').MutationTree<typeof state>}
 */
const mutations = {
  setNodes(state, nodes) {
    state.nodes = nodes;
  },

  setOriginalNodes(state, nodes) {
    state.originalNodes = nodes;

    state.nodesSettingsChangedMapping = state.originalNodes.reduce((settings, node) => {
      settings[node.id] = false;
      return settings;
    }, {});
  },

  setSceneNodesErrors(state, nodeErrors) {
    state.nodeErrors = nodeErrors;
  },

  setNodeErrors(state, {id, nodeErrors}) {
    Vue.set(state.nodeErrors, id, nodeErrors);
  },

  addNode(state, node) {
    state.nodes.push(node);
  },

  updateNode(state, updateNode) {
    state.nodes = state.nodes.map(node => {
      if (node.id === updateNode.id) {
        return cloneDeep(updateNode);
      }

      return node;
    });
  },

  updateNodeSettings(state, updateNode) {
    state.nodes = state.nodes.map(node => {
      if (node.id === updateNode.id) {
        const nodeToUpdate = cloneDeep(node);
        nodeToUpdate.settings = cloneDeep(updateNode.settings);
        return nodeToUpdate;
      }
      return node;
    });
  },

  updateNodeStepName(state, {id, stepName}) {
    state.nodes = state.nodes.map(node => {
      if (node.id === id) {
        const nodeToUpdate = cloneDeep(node);
        nodeToUpdate.stepName = cloneDeep(stepName);
        return nodeToUpdate;
      }
      return node;
    });
  },

  updateNodePosition(state, { nodeId, x, y }) {
    const index = state.nodes.findIndex(n => n.id === nodeId);

    state.nodes[index].x = x;
    state.nodes[index].y = y;
  },

  toggleNodeSelection(state, nodeId) {
    const index = state.nodes.findIndex(n => n.id === nodeId);

    if (index > -1) {
      state.nodes[index].selected = !state.nodes[index].selected;
    }
  },

  deselectNode(state, nodeId) {
    const index = state.nodes.findIndex(n => n.id === nodeId);

    if (index > -1) {
      state.nodes[index].selected = false;
    }

    if (state.nodes[index]?.id === state.selectedNode?.id) {
      state.selectedNode = null;
    }
  },

  removeNode(state, node) {
    state.nodes = state.nodes.filter(n => n.id !== node.id);

    Vue.delete(state.nodeLinkDetails, node.id);
    Vue.delete(state.nodeErrors, node.id);
  },

  setNodeLinkDetails(state, nodeLinkDetails) {
    state.nodeLinkDetails = nodeLinkDetails;
  },

  updateNodeLinkPosition(state, { nodeId, x, y }) {
    if (state.nodeLinkDetails[nodeId]) {
      const nodeLinkDetail = state.nodeLinkDetails[nodeId];

      nodeLinkDetail.x = x;
      nodeLinkDetail.y = y;
    }
  },

  setLinks(state, links) {
    state.links = links;
  },

  addLink(state, link) {
    state.links.push(link);
  },

  setContainer(state, container) {
    state.container = container;
  },

  setName(state, name) {
    state.name = name;
  },

  setNodesValidationMapping(state, validationMapping) {
    state.nodesValidationMapping = validationMapping;
  },

  removeLink(state, linkId) {
    state.links = state.links.filter((value) => {
      return !(value.id === linkId);
    });
  },

  setSelectedNode(state, node) {
    state.selectedNode = node;
  },

  resetScene(state) {
    state.container = {};
    state.nodes = [];
    state.nodeLinkDetails = {};
    state.selectedNode = null;
    state.name = '';
    state.links = [];
    state.nodesSettingsChangedMapping = {};
    state.hasNodeAddedOrRemoved = false;
    state.hasLinksChanged = false;
    state.hasNodePositionChanged = false;
  },

  setHasNodeSettingsChanged(state, { hasNodeSettingsChanged, nodeId }) {
    state.nodesSettingsChangedMapping[nodeId] = hasNodeSettingsChanged;
  },

  setHasNodePositionChanged(state, hasNodePositionChanged) {
    state.hasNodePositionChanged = hasNodePositionChanged;
  },

  setHasNodeAddedOrRemoved(state, hasNodeAddedOrRemoved) {
    state.hasNodeAddedOrRemoved = hasNodeAddedOrRemoved;
  },

  setHasLinksChanged(state, hasLinksChanged) {
    state.hasLinksChanged = hasLinksChanged;
  }
};

/** 
 * @type {import('vuex').ActionTree<typeof state>} 
 */
const actions = {
  prepareScene({ commit, getters, dispatch, state }, callFlow) {
    commit('resetScene');

    const scene = prepareScene(callFlow, getters.availableNodesWithStyling);

    commit('setLinks', scene.links);

    dispatch('prepareNodesAndLinking', scene.nodes);

    dispatch('setSceneNodesErrors', scene.nodes);

    commit('setOriginalNodes', cloneDeep(state.nodes));

    commit('setContainer', scene.container);

    commit('setName', scene.name);
  },

  setCallFlowName({ commit }, callFlowName) {
    commit('setName', callFlowName);
  },

  prepareNodesAndLinking({ commit, getters, state }, nodes) {
    commit('setNodes', prepareNodesAndLinking(nodes, state.links, getters.availableNodesWithStyling));

    commit('setNodeLinkDetails', convertNodesToNodeLinkDetails(state.nodes));
  },

  addNode({ commit, state, dispatch }, { node, originNode, slotNumber, targetSlotNumber, isOnFailure, criteria, filter }) {
    const currEndNode = state.nodes[state.nodes.length - 1];

    let nodeX = node.x ? node.x : currEndNode.x + 250;
    let nodeY = node.y ? node.y : currEndNode.y;

    if (originNode) {
      const nodeLinks = state.links.filter((link) => link.originNode.id == originNode.id);
      const maxY = nodeLinks.length > 0 ? Math.max(...nodeLinks.map(o => o.targetNode.y)) + 200 : 0;
      nodeX = originNode.x + 300;
      nodeY = maxY;
    }

    const newNode = {
      id: node.id,
      x: nodeX,
      y: nodeY,
      isValid: node.isValid,
      nodeTypeId: node.nodeTypeId,
      nodeErrors: node.nodeErrors,
      settings: node.settings,
      stepName: node.stepName,
      nodeOptions: node.nodeOptions,
      newAdded: true
    };

    commit('addNode', newNode);

    commit('setHasNodeAddedOrRemoved', true);

    dispatch('prepareNodesAndLinking', state.nodes);

    dispatch('setNodeErrors', newNode);

    if (originNode) {
      dispatch('addLink', {
        targetNode: state.nodes.find(n => n.id === node.id),
        linkStartData: {
          slotNumber,
          isOnFailure,
          criteria,
          node: originNode,
          weight: originNode.nodeTypeId == 'SplitTestNode' ? 1 : null,
          filter
        },
        slotNumber: targetSlotNumber
      });
    }
  },

  setSceneNodesErrors({ commit, state }) {
    const nodeErrors = state.nodes.reduce((nodesValidations, node) => {
      const nodeErrors = validateNode(node, state.nodesValidationMapping[node.nodeTypeId], state.links.filter(l => l.originNode?.id === node.id));

      nodesValidations[node.id] = nodeErrors;

      return nodesValidations;
    }, {});

    commit('setSceneNodesErrors', nodeErrors);
  },

  setNodesValidationMapping({ commit, state, rootState }) {
    const availableNodes = rootState['callFlows'].availableNodes;
    const nodeValidationFields = {};
    availableNodes.forEach(node => {
      nodeValidationFields[node.nodeTypeId] = Object.freeze({
        ...getParameterValidationFields(node.parameters),
        canBeLeaf: node.canBeLeaf
      });
    });

    commit('setNodesValidationMapping', nodeValidationFields);
  },

  setNodeErrors({ commit, state }, node) {
    const nodeErrors = validateNode(node, state.nodesValidationMapping[node.nodeTypeId], state.links.filter(l => l.originNode?.id === node.id));
    commit('setNodeErrors', { id: node.id, nodeErrors });
  },

  updateNodeStepName({ commit }, node) {
    commit('updateNodeStepName', node);
  },

  updateNodeSettings({ commit, dispatch }, node) {
    commit('updateNodeSettings', node);

    dispatch('setNodeErrors', node);

    dispatch('checkIfNodeSettingsChanged', node);
  },

  updateNodePosition({ commit }, { nodeId, x, y }) {
    commit('updateNodePosition', { nodeId, x, y });
    commit('updateNodeLinkPosition', { nodeId, x, y });
    commit('setHasNodePositionChanged', true);
  },

  checkIfNodeSettingsChanged({ commit, state }, node) {
    const oldNode = state.originalNodes.find(n => n.id === node.id);

    if (oldNode) {
      commit('setHasNodeSettingsChanged', { nodeId: node.id, hasNodeSettingsChanged: !areNodeSettingsEqual(oldNode, node) });
    }
  },

  addLink({ state, commit, dispatch }, { targetNode, linkStartData, slotNumber }) {
    if (linkStartData && targetNode && slotNumber > -1) {
      const link = state.links.find((value) => {
        return value.targetNode.id === targetNode.id && value.targetSlot === slotNumber;
      });

      // Remove existing link
      dispatch('removeLink', link?.id);

      const maxID = Math.max(
        0,
        ...state.links.map((o) => {
          return o.id;
        })
      );

      // skip if looping
      if (linkStartData.node.id !== targetNode.id) {
        const newLink = {
          id: maxID + 1,
          originSlot: linkStartData.slotNumber,
          originNode: linkStartData.node,
          criteria: linkStartData.isOnFailure ? [] : buildCriteria(linkStartData),
          filter: linkStartData.filter,
          onSuccess: !linkStartData.isOnFailure,
          isOnFailure: linkStartData.isOnFailure,
          weight: linkStartData.weight,
          targetSlot: slotNumber,
          targetNode: targetNode
        };

        commit('addLink', newLink);

        commit('setHasLinksChanged', true);

        if (linkStartData.node.nodeTypeId === 'RouterNode' || linkStartData.node.nodeTypeId === 'SplitTestNode') {
          const routerNodeLinks = state.links.filter((scenelink) => scenelink.originNode.id == linkStartData.node.id);

          dispatch('updateLinks', { routes: routerNodeLinks, reorderRoutes: linkStartData.node.nodeTypeId === 'RouterNode' });
        }
        const originNode = state.nodes.find((node) => node.id === newLink.originNode.id);

        dispatch('prepareNodesAndLinking', state.nodes);
        dispatch('checkNodeHasChildAndUpdate', originNode);

        if (newLink.targetNode) {
          const targetNode = state.nodes.find((node) => node.id === newLink.targetNode.id);
          dispatch('setNodeErrors', targetNode);
        }
      }
    }
  },

  removeLink({ commit, state, dispatch }, linkId) {
    if (linkId === null || linkId === undefined) {
      return;
    }

    const linkDetails = state.links.find(l => l.id === linkId);

    commit('removeLink', linkId);

    commit('setHasLinksChanged', true);

    if (linkDetails) {
      dispatch('prepareNodesAndLinking', state.nodes);

      const originNode = state.nodes.find(node => node.id === linkDetails.originNode.id);
      const targetNode = state.nodes.find(node => node.id === linkDetails.targetNode.id);

      if (targetNode) {
        dispatch('setNodeErrors', targetNode);
      }

      dispatch('checkNodeHasChildAndUpdate', originNode);

    }
  },

  checkNodeHasChildAndUpdate({ commit, state, dispatch }, node) {
    if (!node) {
      return;
    }

    const hasChild = state.links.some(l => l.originNode?.id === node?.id);

    const clonedNode = {
      ...node,
      hasChild
    };

    dispatch('setNodeErrors', clonedNode);
    commit('updateNode', clonedNode);
  },

  removeNode({ commit, dispatch }, node) {
    commit('removeNode', node);

    dispatch('removeLinksFromNode', node);

    commit('setHasNodeAddedOrRemoved', true);
  },

  removeLinksFromNode({ state, dispatch }, node) {
    state.links.forEach(link => {
      if (link.originNode.id == node.id || link.targetNode.id == node.id) {
        dispatch('removeLink', link.id);
        return;
      }
    });
  },

  updateLinks({ commit, state }, { routes, reorderRoutes }) {
    let order = 1;
    const newLinks = cloneDeep(state.links);

    // index of router node whose nodes need re-ordering
    const currentRouterIndex = newLinks.findIndex(l => l.targetNode.id === routes[0].originNode.id);

    // extract the portion that needs sorting (i.e the router nodes)
    const routesLink = []

    // looping through the newLink array to find, update and extract the routes nodes 
    for (const route of routes) {
      const linkName = route.name;

      let routeName;
      if (reorderRoutes) {
        routeName = route.name?.includes('Press') ? route.name : 'Route: ' + order;
      } else {
        routeName = route.name ? route.name : 'Route: ' + order;
      }

      // updating values and assigning order for sorting
      const linkValidationErrors = validateLinkCriterias(route);

      let index = newLinks.findIndex(scenelink => scenelink.id == route.id);
      const link = newLinks.find(scenelink => scenelink.id == route.id);
      link.criteria = route.criteria;
      link.filter = route.filter;
      link.name = routeName;
      link.order = order++;
      link.isValid = linkValidationErrors.isSuccessful;
      link.weight = route.weight;

      //extracting the element from newLinks
      routesLink.push((newLinks.splice(index, 1))[0])
    }

    // sorting the routesLink array
    routesLink.sort((a, b) => a.order - b.order);

    // inserting this sub-array back at its place in newLinks
    newLinks.splice(currentRouterIndex + 1, 0, ...routesLink);

    commit('setLinks', newLinks);
    commit('setHasLinksChanged', true);
  },

  setSelectedNode({ commit, dispatch }, node) {
    const isSelected = !node.selected;

    commit('toggleNodeSelection', node.id);

    if (isSelected) {
      const selectedNode = cloneDeep(node);

      selectedNode.settings = selectedNode.settings || {};

      commit('setSelectedNode', selectedNode);

      dispatch('deselectAllNodes', selectedNode.id);
    } else {
      commit('setSelectedNode', null);
    }
  },

  deselectAllNodes({ commit, state }, exceptNodeId = null) {
    state.nodes.forEach(node => {
      if (node.id !== exceptNodeId && node.selected) {
        commit('deselectNode', node.id);
      }
    });
  }
};

export default {
  actions,
  state,
  mutations,
  getters
};